import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Building2 } from 'lucide-react';
import CompanyForm from '../shared/forms/CompanyForm';

/**
 * Company Onboarding Component
 * Production-quality empty state with Headless UI modal
 * 
 * Features:
 * - Smooth animations with backdrop blur
 * - Proper focus management
 * - Enhanced empty state design
 * - Accessible modal dialog
 */
const CompanyOnboarding = ({ createCompany, loading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);

  const handleCreateCompany = async (companyData) => {
    setError(null);
    
    const result = await createCompany(companyData);
    
    if (result.success) {
      setIsOpen(false);
      // The useCompany hook will automatically update state
    } else {
      setError(result.error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  return (
    <>
      {/* Empty State */}
      <div className="max-w-[2000px] w-full mx-auto px-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Company</h1>
        </div>
        
        <div className="flex justify-center items-center py-20">
          <div className="text-center max-w-md">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center shadow-lg">
              <Building2 className="h-12 w-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">No Company Found</h2>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">
              Create your company to manage employees, teams, and business operations.
            </p>
            <button
              onClick={() => setIsOpen(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Create Company
            </button>
          </div>
        </div>
      </div>

      {/* Company Creation Modal - Headless UI */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop with blur */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          {/* Modal Container */}
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all w-full sm:my-8 sm:w-full sm:max-w-2xl">
                  {/* Fixed Header */}
                  <div className="bg-white px-6 py-5 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                        Create Company
                      </Dialog.Title>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                          onClick={handleClose}
                          disabled={loading}
                        >
                          <span className="sr-only">Close</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div className="max-h-[calc(100vh-16rem)] sm:max-h-[32rem] overflow-y-auto">
                    <div className="px-6 py-6">
                      <CompanyForm
                        onSubmit={handleCreateCompany}
                        submitLabel="Create Company"
                        loading={loading}
                        error={error}
                        showTitle={false}
                        requireTimezone={false}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default CompanyOnboarding;